import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import LoadingButton from "../LoadingButton/LoadingButton";
import { OrbitLoaderComponent } from "../../utils/orbitLoader";
import "./WorkScheduleContent.css";

// Türkçe gün adları ve renkler - DÜZELTİLDİ
const turkishDays = {
  Mon: "Pzt",
  Tue: "Sal",
  Wed: "Çar",
  Thu: "Per",
  Fri: "Cum",
  Sat: "Cmt",
  Sun: "Paz",
};

const dayColors = {
  Pzt: "#FF6B6B", // Pazartesi - Kırmızı
  Sal: "#4ECDC4", // Salı - Turkuaz
  Çar: "#FFD166", // Çarşamba - Sarı
  Per: "#118AB2", // Perşembe - Mavi
  Cum: "#073B4C", // Cuma - Lacivert
  Cmt: "#6A4C93", // Cumartesi - Mor
  Paz: "#F15BB5", // Pazar - Pembe
};

// Günlerin renkli daire içinde gösterimi için bileşen
const DayCircle = ({ day, size = "default" }) => {
  // Türkçe karşılığı al
  const turkishDay = turkishDays[day] || day;
  const color = dayColors[turkishDay] || "#CCCCCC"; // Varsayılan gri
  const circleSize = size === "small" ? 20 : 30; // Küçük ve normal boyutlar
  return (
    <div
      className="day-circle"
      style={{
        backgroundColor: color,
        width: circleSize,
        height: circleSize,
        lineHeight: circleSize + "px",
        fontSize: size === "small" ? 10 : 14,
      }}
    >
      {turkishDay}
    </div>
  );
};

const WorkScheduleContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [schedules, setSchedules] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [viewMode, setViewMode] = useState("calendar");
  const [currentMonth, setCurrentMonth] = useState("");
  const [formData, setFormData] = useState({
    date: "",
    start_time: "",
    end_time: "",
    days: ["Mon", "Tue", "Wed", "Thu", "Fri"], // Varsayılan günler
  });
  const [formLoading, setFormLoading] = useState(false);
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    type: "",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [editingSchedule, setEditingSchedule] = useState(null); // Düzenleme modu için

  useEffect(() => {
    fetchUsers();
    if (location.state?.userId) {
      setSelectedUser(location.state.userId);
      fetchSchedules(location.state.userId);
    }
    // Ay adını ayarla
    const monthNames = [
      "Ocak",
      "Şubat",
      "Mart",
      "Nisan",
      "Mayıs",
      "Haziran",
      "Temmuz",
      "Ağustos",
      "Eylül",
      "Ekim",
      "Kasım",
      "Aralık",
    ];
    const now = new Date();
    setCurrentMonth(`${monthNames[now.getMonth()]} ${now.getFullYear()}`);
  }, [location]);

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from("users")
        .select("user_id, name, surname")
        .eq("is_active", true)
        .order("name");
      if (!error && data) {
        setUsers(data);
      }
    } catch (error) {
      console.error("Kullanıcılar yüklenemedi:", error.message);
    }
  };

  const fetchSchedules = async (userId) => {
    if (!userId) return;
    setLoading(true);
    try {
      let query = supabase.from("work_schedules").select("*");
      if (userId !== "all") {
        query = query.eq("user_id", userId);
      }
      const { data, error } = await query.order("date", { ascending: true });
      if (!error) {
        setSchedules(data || []);
      }
    } catch (error) {
      console.error("Mesai takvimi yüklenemedi:", error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUserChange = (e) => {
    const userId = e.target.value;
    setSelectedUser(userId);
    if (userId) {
      fetchSchedules(userId);
    } else {
      setSchedules([]);
    }
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    const formattedDate = date.toISOString().split("T")[0];
    setFormData((prev) => ({
      ...prev,
      date: formattedDate,
    }));
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Gün seçimi için fonksiyon
  const handleDayToggle = (day) => {
    setFormData((prev) => {
      const days = [...prev.days];
      if (days.includes(day)) {
        return { ...prev, days: days.filter((d) => d !== day) };
      } else {
        return { ...prev, days: [...days, day] };
      }
    });
  };

  const changeMonth = (direction) => {
    const newDate = new Date(selectedDate);
    if (direction === "prev") {
      newDate.setMonth(newDate.getMonth() - 1);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    setSelectedDate(newDate);
    handleDateChange(newDate);
    // Ay adını güncelle
    const monthNames = [
      "Ocak",
      "Şubat",
      "Mart",
      "Nisan",
      "Mayıs",
      "Haziran",
      "Temmuz",
      "Ağustos",
      "Eylül",
      "Ekim",
      "Kasım",
      "Aralık",
    ];
    setCurrentMonth(
      `${monthNames[newDate.getMonth()]} ${newDate.getFullYear()}`
    );
  };

  const goToToday = () => {
    const today = new Date();
    setSelectedDate(today);
    handleDateChange(today);
    // Ay adını güncelle
    const monthNames = [
      "Ocak",
      "Şubat",
      "Mart",
      "Nisan",
      "Mayıs",
      "Haziran",
      "Temmuz",
      "Ağustos",
      "Eylül",
      "Ekim",
      "Kasım",
      "Aralık",
    ];
    setCurrentMonth(`${monthNames[today.getMonth()]} ${today.getFullYear()}`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedUser || selectedUser === "all") {
      setNotification({
        show: true,
        message: "Lütfen bir personel seçin",
        type: "error",
      });
      setTimeout(() => {
        setNotification({ show: false, message: "", type: "" });
      }, 3000);
      return;
    }
    setFormLoading(true);
    try {
      let result;

      // Düzenleme modunda mıyız?
      if (editingSchedule) {
        // Mevcut kaydı güncelle
        const { data, error } = await supabase
          .from("work_schedules")
          .update({
            user_id: selectedUser,
            date: formData.date,
            start_time: formData.start_time,
            end_time: formData.end_time,
            days: formData.days,
            updated_at: new Date().toISOString(),
          })
          .eq("schedule_id", editingSchedule.schedule_id);

        if (error) throw error;
        result = data;

        setNotification({
          show: true,
          message: "Mesai kaydı başarıyla güncellendi",
          type: "success",
        });
      } else {
        // Yeni kayıt ekle
        const { data, error } = await supabase.from("work_schedules").insert([
          {
            user_id: selectedUser,
            date: formData.date,
            start_time: formData.start_time,
            end_time: formData.end_time,
            days: formData.days,
          },
        ]);

        if (error) throw error;
        result = data;

        setNotification({
          show: true,
          message: "Mesai kaydı başarıyla oluşturuldu",
          type: "success",
        });
      }

      setShowModal(false);
      setEditingSchedule(null); // Düzenleme modunu sıfırla
      fetchSchedules(selectedUser);
      setFormData({
        date: "",
        start_time: "",
        end_time: "",
        days: ["Mon", "Tue", "Wed", "Thu", "Fri"], // Varsayılan günler
      });

      // Olayı tetikle
      window.dispatchEvent(
        new CustomEvent("workScheduleUpdated", {
          detail: { userId: selectedUser },
        })
      );
    } catch (error) {
      setNotification({
        show: true,
        message: "İşlem başarısız: " + error.message,
        type: "error",
      });
    } finally {
      setFormLoading(false);
      setTimeout(() => {
        setNotification({ show: false, message: "", type: "" });
      }, 3000);
    }
  };

  const deleteSchedule = async (scheduleId) => {
    if (window.confirm("Bu mesai kaydını silmek istediğinize emin misiniz?")) {
      try {
        const { error } = await supabase
          .from("work_schedules")
          .delete()
          .eq("schedule_id", scheduleId);
        if (error) throw error;
        setNotification({
          show: true,
          message: "Mesai kaydı silindi",
          type: "success",
        });
        fetchSchedules(selectedUser);
        window.dispatchEvent(
          new CustomEvent("workScheduleUpdated", {
            detail: { userId: selectedUser },
          })
        );
      } catch (error) {
        setNotification({
          show: true,
          message: "Mesai kaydı silinemedi: " + error.message,
          type: "error",
        });
      }
    }
  };

  // Düzenleme butonuna tıklandığında
  const handleEditSchedule = (schedule) => {
    setEditingSchedule(schedule);
    setFormData({
      date: schedule.date,
      start_time: schedule.start_time,
      end_time: schedule.end_time,
      days: schedule.days || ["Mon", "Tue", "Wed", "Thu", "Fri"],
    });
    setShowModal(true);
  };

  const getScheduleForDate = (date) => {
    const dateStr = date.toISOString().split("T")[0];
    return schedules.filter((s) => s.date === dateStr);
  };

  const tileContent = ({ date, view }) => {
    if (view === "month") {
      const dateSchedules = getScheduleForDate(date);
      if (dateSchedules.length > 0) {
        return (
          <div className="schedule-indicators">
            {dateSchedules.map((schedule, index) => (
              <div
                key={index}
                className={`schedule-dot work`}
                title={`Mesai: ${schedule.start_time} - ${schedule.end_time}`}
              ></div>
            ))}
          </div>
        );
      }
    }
    return null;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("tr-TR");
  };

  const formatTime = (timeString) => {
    return timeString.substring(0, 5);
  };

  const getSelectedUserName = () => {
    if (!selectedUser) return "";
    if (selectedUser === "all") return "Tüm Personeller";
    const user = users.find((u) => u.user_id === selectedUser);
    return user ? `${user.name} ${user.surname}` : "";
  };

  const getUserNameById = (userId) => {
    const user = users.find((u) => u.user_id === userId);
    return user ? `${user.name} ${user.surname}` : "Bilinmeyen Kullanıcı";
  };

  const getFilteredSchedules = () => {
    if (!searchTerm) return schedules;
    return schedules.filter((schedule) => {
      const userName = getUserNameById(schedule.user_id).toLowerCase();
      const searchLower = searchTerm.toLowerCase();
      return userName.includes(searchLower);
    });
  };

  if (loading) {
    return (
      <div className="loading-container">
        <OrbitLoaderComponent size="medium" showPath={true} />
        <p className="loading-text">Yükleniyor...</p>
      </div>
    );
  }

  const filteredSchedules = getFilteredSchedules();

  return (
    <div className="work-schedule-content">
      <div className="schedule-header">
        <div className="header-info">
          <h2>Mesai Takvimi</h2>
          <p>Personel mesai düzenlemelerini yönetin</p>
        </div>
        <div className="header-controls">
          <div className="view-toggle">
            <button
              className={`view-btn ${viewMode === "calendar" ? "active" : ""}`}
              onClick={() => setViewMode("calendar")}
            >
              <i className="fas fa-calendar-alt"></i>
            </button>
            <button
              className={`view-btn ${viewMode === "list" ? "active" : ""}`}
              onClick={() => setViewMode("list")}
            >
              <i className="fas fa-list"></i>
            </button>
          </div>
          <div className="user-select">
            <div className="select-wrapper">
              <select
                value={selectedUser}
                onChange={handleUserChange}
                className="filter-select"
              >
                <option value="">Personel Seçin</option>
                <option value="all">Tüm Personeller</option>
                {users.map((user) => (
                  <option key={user.user_id} value={user.user_id}>
                    {user.name} {user.surname}
                  </option>
                ))}
              </select>
              <i className="fas fa-chevron-down select-arrow"></i>
            </div>
          </div>
        </div>
      </div>
      {selectedUser && (
        <div className="selected-user-info">
          <i className="fas fa-user"></i>
          <span>Seçili Personel: {getSelectedUserName()}</span>
        </div>
      )}
      {notification.show && (
        <div className={`notification ${notification.type}`}>
          {notification.message}
        </div>
      )}
      <div className="schedule-container">
        {viewMode === "calendar" ? (
          <div className="calendar-container enhanced">
            <div className="calendar-header">
              <div className="month-navigation">
                <button className="nav-btn" onClick={() => changeMonth("prev")}>
                  <i className="fas fa-chevron-left"></i>
                </button>
                <h3>{currentMonth}</h3>
                <button className="nav-btn" onClick={() => changeMonth("next")}>
                  <i className="fas fa-chevron-right"></i>
                </button>
              </div>
              <div className="calendar-actions">
                <button
                  className="calendar-action-btn today-btn"
                  onClick={goToToday}
                >
                  Bugün
                </button>
                <button
                  className="calendar-action-btn add-schedule-btn"
                  onClick={() => {
                    const formattedDate = selectedDate
                      .toISOString()
                      .split("T")[0];
                    setFormData((prev) => ({
                      ...prev,
                      date: formattedDate,
                    }));
                    setEditingSchedule(null); // Yeni kayıt modu
                    setShowModal(true);
                  }}
                  disabled={!selectedUser || selectedUser === "all"}
                >
                  <i className="fas fa-plus"></i> Ekle
                </button>
              </div>
            </div>
            <Calendar
              onChange={handleDateChange}
              value={selectedDate}
              tileContent={tileContent}
              locale="tr-TR"
              className="modern-calendar enhanced"
            />
          </div>
        ) : (
          <div className="schedule-list enhanced">
            <div className="list-header">
              <h3>Mesai Listesi</h3>
              <div className="list-actions">
                <div className="search-input-wrapper">
                  <i className="fas fa-search"></i>
                  <input
                    type="text"
                    placeholder="Mesai ara..."
                    className="schedule-search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setFormData({
                      date: new Date().toISOString().split("T")[0],
                      start_time: "09:00",
                      end_time: "18:00",
                      days: ["Mon", "Tue", "Wed", "Thu", "Fri"],
                    });
                    setEditingSchedule(null); // Yeni kayıt modu
                    setShowModal(true);
                  }}
                  disabled={!selectedUser || selectedUser === "all"}
                >
                  <i className="fas fa-plus"></i> Yeni Mesai
                </button>
              </div>
            </div>
            <div className="schedule-items enhanced">
              {filteredSchedules.map((schedule) => (
                <div key={schedule.schedule_id} className="schedule-item">
                  <div className="schedule-date">
                    <div className="date-day">{formatDate(schedule.date)}</div>
                    <div className="date-time">
                      {formatTime(schedule.start_time)} -{" "}
                      {formatTime(schedule.end_time)}
                    </div>
                  </div>
                  <div className="schedule-details">
                    {selectedUser === "all" && (
                      <div className="schedule-user">
                        <i className="fas fa-user"></i>
                        <span>{getUserNameById(schedule.user_id)}</span>
                      </div>
                    )}
                    <div className="schedule-days">
                      {schedule.days &&
                        schedule.days.map((day, index) => (
                          <DayCircle key={index} day={day} size="small" />
                        ))}
                    </div>
                  </div>
                  <div className="schedule-actions">
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => handleEditSchedule(schedule)}
                      title="Düzenle"
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => deleteSchedule(schedule.schedule_id)}
                      title="Sil"
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                </div>
              ))}
              {filteredSchedules.length === 0 && (
                <div className="no-data">
                  <i className="fas fa-briefcase"></i>
                  <p>
                    {selectedUser
                      ? selectedUser === "all"
                        ? "Henüz kayıt bulunmuyor"
                        : "Henüz mesai kaydı bulunmuyor"
                      : "Lütfen bir personel seçin"}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editingSchedule ? "Mesai Düzenle" : "Mesai Ekle"}</h3>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingSchedule(null);
                }}
                className="close-button"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Personel</label>
                  <div className="select-wrapper">
                    <select
                      value={selectedUser}
                      onChange={handleUserChange}
                      required
                      disabled={!!location.state?.userId}
                      className="form-select"
                    >
                      <option value="">Personel Seçin</option>
                      {users.map((user) => (
                        <option key={user.user_id} value={user.user_id}>
                          {user.name} {user.surname}
                        </option>
                      ))}
                    </select>
                    <i className="fas fa-chevron-down select-arrow"></i>
                  </div>
                </div>
                <div className="form-group">
                  <label>Tarih</label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleFormChange}
                    required
                    className="form-input"
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Başlangıç Saati</label>
                    <input
                      type="time"
                      name="start_time"
                      value={formData.start_time}
                      onChange={handleFormChange}
                      required
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label>Bitiş Saati</label>
                    <input
                      type="time"
                      name="end_time"
                      value={formData.end_time}
                      onChange={handleFormChange}
                      required
                      className="form-input"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Çalışma Günleri</label>
                  <div className="days-selector">
                    {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(
                      (day) => (
                        <label key={day} className="day-checkbox">
                          <input
                            type="checkbox"
                            name="days"
                            value={day}
                            checked={formData.days.includes(day)}
                            onChange={() => handleDayToggle(day)}
                          />
                          <DayCircle day={day} size="small" />
                          <span>{turkishDays[day]}</span>
                        </label>
                      )
                    )}
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingSchedule(null);
                    }}
                    className="btn btn-secondary"
                  >
                    İptal
                  </button>
                  <LoadingButton
                    type="submit"
                    className="btn btn-primary"
                    isLoading={formLoading}
                  >
                    {editingSchedule ? "Güncelle" : "Mesai Ekle"}
                  </LoadingButton>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkScheduleContent;
